/**
 * Vertical scrolling of the grid is virtual: the scroll container never scrolls vertically (it
 * only scrolls horizontally, natively). The grid keeps one logical scroll offset in pixels over
 * the full logical height of the rows, positions the visible rows from it, and draws its own
 * scrollbar. That removes the browser's element-height limit (~16–33M px, a few hundred thousand
 * rows) and with it the need to map a shortened scroll range onto the table.
 */

/** Minimum thumb length, so a million-row table still has something to grab. */
export const MIN_THUMB_HEIGHT = 28

/** How far the logical offset can go: the last row ends at the bottom of the rows area. */
export const getMaxScrollTop = (logicalTotalHeight: number, rowsAreaHeight: number): number =>
  Math.max(0, logicalTotalHeight - rowsAreaHeight)

export const clampScrollTop = (scrollTop: number, maxScrollTop: number): number => {
  if (!(scrollTop > 0)) return 0
  return scrollTop >= maxScrollTop ? maxScrollTop : scrollTop
}

export interface ScrollbarGeometry {
  thumbHeight: number
  thumbTop: number
}

/** Where the thumb sits in a track of `trackHeight` px for the given scroll state. */
export const scrollbarGeometry = (
  logicalTotalHeight: number,
  rowsAreaHeight: number,
  scrollTop: number,
  trackHeight: number
): ScrollbarGeometry => {
  if (logicalTotalHeight <= 0 || trackHeight <= 0) return { thumbHeight: trackHeight, thumbTop: 0 }
  const thumbHeight = Math.min(
    trackHeight,
    Math.max(MIN_THUMB_HEIGHT, (rowsAreaHeight / logicalTotalHeight) * trackHeight)
  )
  const maxScrollTop = getMaxScrollTop(logicalTotalHeight, rowsAreaHeight)
  const ratio = maxScrollTop > 0 ? clampScrollTop(scrollTop, maxScrollTop) / maxScrollTop : 0
  return { thumbHeight, thumbTop: ratio * (trackHeight - thumbHeight) }
}

/** The logical offset a thumb at `thumbTop` stands for; the inverse of scrollbarGeometry. */
export const scrollTopFromThumb = (
  thumbTop: number,
  thumbHeight: number,
  trackHeight: number,
  maxScrollTop: number
): number => {
  const travel = trackHeight - thumbHeight
  if (travel <= 0) return 0
  return clampScrollTop((thumbTop / travel) * maxScrollTop, maxScrollTop)
}
